import AsyncSelect from "react-select/async";
import { components } from "react-select";
import Fuse from "fuse.js";
import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";

import { searchRequests } from "@/api/(requests)/search";

import { adminRoute } from "../../../config/roles";
import Badge from "../../UI/Badge";
import { getAutomationDataAttr } from "../../../utils/automationUtils";
import images from "../../../utils/images";
import { toTitleCase } from "../../../utils/filters";
import { useDebounce } from "../../../utils/debounce";

import "./styles.scss";

let SEARCH_OPTIONS = [
  {
    label: "PRACTICE",
    options: [],
  },
  {
    label: "PATIENT",
    options: [],
  },
  {
    label: "PROVIDER",
    options: [],
  },
  {
    label: "REFERRAL",
    options: [],
  },
  {
    label: "QUESTIONS",
    options: [],
  },
];

const Omnisearch = (props) => {
  const [searchText, setSearchText] = useState("");
  const [isFocused, setIsFocused] = useState(props.startFocused ? props.startFocused : false);
  const inputRef = useRef(null);

  // Derived, not stored — props.questions is the only source of truth. Guarded against
  // non-array responses (e.g. admin:csm not getting questions back) crashing the .map below.
  const omnisearchQuestions = Array.isArray(props.questions) ? props.questions : [];

  const onInputChange = (query, { action }) => {
    if (action !== "set-value" && action !== "menu-close") {
      setSearchText(query);

      return query;
    }

    return searchText;
  };

  useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFocused]);

  const handleSelection = async (selectedOption) => {
    if (inputRef.current) {
      inputRef.current.focus();
    }

    if (!selectedOption) {
      return;
    }

    let route;
    let search;

    switch (selectedOption.type) {
      case "Practice": {
        route = `${adminRoute}/practice/${selectedOption.value.id}`;
        break;
      }

      case "Patient": {
        route = `${adminRoute}/patient/${selectedOption.value.username}/profile/v2`;
        break;
      }

      case "Provider": {
        route = `${adminRoute}/practice/${selectedOption.value.practice_id}/provider/${selectedOption.value.username}`;

        break;
      }

      case "Referral": {
        route = `${adminRoute}/referral/${selectedOption.value.referralCode}`;

        break;
      }

      case "Question": {
        route = `${adminRoute}/search`;
        search = `?questionId=${selectedOption.value.questionId}&detailLevel=${
          selectedOption.value.detail_level
        }&importantFields=${selectedOption.value.important_fields.join(",")}`;
        break;
      }
    }

    if (route != null) {
      props.history.replace({ pathname: route, search });
      setSearchText("");
      if (props.externalToggle) {
        props.externalToggle();
      }
    }
  };

  const topNFuzzySearch = (stuff, keys, threshold, query, takeN) => {
    // Fuzzy search for questions
    const fuzzySearchOptions = {
      ignoreLocation: true,
      includeScore: true,
      keys: keys,
      threshold: threshold,
    };
    const fuzzySearch = new Fuse(stuff, fuzzySearchOptions);

    const fuzzyQuestions = fuzzySearch.search(query);

    // Sort by score ascending (lower score is higher match)
    fuzzyQuestions.sort((a, b) => a.score > b.score);

    return fuzzyQuestions.map((question) => question.item).slice(0, takeN);
  };

  const handleSearch = async (query) => {
    // Not enough data to guess at the question
    if (query.length < 3) {
      return [];
    }

    let data = {
      query,
    };

    let response = await searchRequests.search_omni({ context: { showLoader: false }, data });

    let searchOptions = SEARCH_OPTIONS.slice();

    const formatPatientName = (patient) => {
      let name = `${patient.first_name} ${patient.last_name}`;

      if (patient.preferred_name) {
        name += ` (${patient.preferred_name})`;
      }

      return name;
    };

    searchOptions[0].options = response["practices"].map((practice) => {
      return {
        detail_level: practice.detail_level,
        label: `${practice.name}`,
        type: "Practice",
        value: practice,
      };
    });
    searchOptions[1].options = response["patients"].map((patient) => {
      return {
        detail_level: patient.detail_level,
        label: formatPatientName(patient),
        subtext: patient.dob,
        type: "Patient",
        value: patient,
      };
    });
    searchOptions[2].options = response["providers"].map((provider) => {
      return {
        detail_level: provider.detail_level,
        label: `${provider.name}`,
        type: "Provider",
        value: provider,
      };
    });
    searchOptions[3].options = response["referrals"].map((referral) => {
      return {
        detail_level: referral.detail_level,
        label: `${referral.first_name} ${referral.last_name}`,
        subtext: referral.dob,
        type: "Referral",
        value: referral,
      };
    });

    const omniQuestions = omnisearchQuestions.map((osQuestion) => {
      return {
        detail_level: osQuestion.detail_level,
        label: osQuestion.label,
        type: "Question",
        value: osQuestion,
      };
    });

    // Only top 5 matching.
    searchOptions[4].options = topNFuzzySearch(omniQuestions, ["label"], 0.9, query, 5);

    return searchOptions;
  };

  const formatOptionLabel = (option) => {
    let { label, subtext, type, value } = option;
    let icon,
      badge = undefined;

    if (type === "Practice") {
      icon = <img alt="patient-avatar" className="avatar" src={images("./common/suitcase.svg")} />;
      badge = <div />;
    }

    if (type === "Patient") {
      icon = <img alt="patient-avatar" className="avatar" src={images("./common/avatar.png")} />;
      badge = (
        <div className="d-flex badge-group">
          <Badge text={type} type="light-grey" />
          <Badge text={toTitleCase(value.status)} type="success" />
        </div>
      );
    }

    if (type === "Provider") {
      icon = <img alt="patient-avatar" className="avatar" src={images("./common/avatar.png")} />;
      let provider_type = value.provider_type;

      if (value.provider_type !== "PCP") {
        provider_type = toTitleCase(provider_type.replace("_", " "));
      }
      badge = (
        <div className="d-flex badge-group">
          <Badge classes="Badge--provider" text={provider_type} type="light-grey" />
        </div>
      );
    }

    if (type === "Referral") {
      icon = <img alt="patient-avatar" className="avatar" src={images("./common/avatar.png")} />;
      badge = (
        <div className="d-flex badge-group">
          <Badge text={value.referral_status} type="light-grey" />
        </div>
      );
    }

    return (
      <div
        className="d-flex justify-content-between"
        {...getAutomationDataAttr(`OmnisearchResult${type}`)}
      >
        <div className="d-flex">
          {icon}
          <div>{label}</div>
          <div className="Omnisearch--option-label-subtext">{subtext}</div>
        </div>
        {badge}
      </div>
    );
  };

  const dropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <img
          alt="search icon"
          onClick={() => setIsFocused(!isFocused)}
          src={images("./common/search.svg")}
        />
      </components.DropdownIndicator>
    );
  };

  const noOptionsMessage = (props) => {
    return (
      <components.NoOptionsMessage {...props}>
        <img alt="no results" className="m-2" src={images("./common/no-search-results.svg")} />
        <div className="m-2">We couldn’t find anything matching your search.</div>
      </components.NoOptionsMessage>
    );
  };

  const displayNoOptions = searchText === "" ? () => null : noOptionsMessage;
  const nonFocusedSelect = (
    <span className="mt-2 mr-2 search-icon" onClick={() => setIsFocused(!isFocused)}></span>
  );

  const debounceLoadOptions = useDebounce((query) => {
    if (searchText) {
      return handleSearch(query);
    }
  }, 500);

  const focusedSelect = (
    <AsyncSelect
      className="Omnisearch mr-2"
      components={{
        DropdownIndicator: dropdownIndicator,
        NoOptionsMessage: noOptionsMessage,
      }}
      formatOptionLabel={formatOptionLabel}
      id="omnisearch-input"
      inputValue={searchText}
      isClearable={true}
      isSearchable={true}
      loadOptions={debounceLoadOptions}
      menuPosition="fixed"
      noOptionsMessage={displayNoOptions}
      onBlurResetsInput={false}
      onChange={handleSelection}
      onInputChange={onInputChange}
      onSelectResetsInput={false}
      placeholder="Search by name, ID, email, or phone (e.g. 5125551234)"
      ref={inputRef}
      value={searchText}
    />
  );

  let renderedOutput = isFocused ? focusedSelect : nonFocusedSelect;

  if (props.searchBarOnly) {
    renderedOutput = focusedSelect;
  }

  return renderedOutput;
};

export default withRouter(Omnisearch);
